/**
 * Pure functions for the Governance Pulse section (RAISE-16886).
 *
 * No React imports — fully unit-testable without jsdom.
 */

import type { ProjectStats } from "@/hooks/useProjectStats";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AdrStatus = "accepted" | "draft" | "deprecated" | "superseded";

export interface PulseInputs {
  /** Null when stats endpoint is unavailable or returns 403/error. */
  stats: ProjectStats | null;
  /** Number of artifacts where type === "adr". Always available from artifact list. */
  adrCount: number;
  /** Null when pattern-health endpoint returns 403 (plan-gated). */
  patternCount: number | null;
}

export interface PulseData {
  score: number | null;
  adrCount: number;
  /** Null when coverage is unavailable (stats missing). */
  coveragePct: number | null;
}

export interface AttentionItem {
  /** Unique stable key for React key prop. */
  key: string;
  /** i18n key under projectPortal.governance.pulse.attention.* */
  message: string;
  severity: "warn" | "info";
}

// ---------------------------------------------------------------------------
// Internal weight constants
// ---------------------------------------------------------------------------

const WEIGHT_GATES = 40;
const WEIGHT_COVERAGE = 30;
const WEIGHT_DECISIONS = 15;
const WEIGHT_GRAPH = 15;

// ---------------------------------------------------------------------------
// computeCompositeScore
// ---------------------------------------------------------------------------

/**
 * Weighted average of up to 4 governance signals (D1).
 *
 * - Gates 40%: gate_pass_rate.pass / (pass + fail) × 100
 * - Coverage 30%: artifact_coverage_pct (direct 0–100)
 * - Decisions 15%: min(adrCount / 5, 1) × 100
 * - Graph 15%: min(patternCount / 10, 1) × 100
 *
 * If a signal source is unavailable, that signal's weight is excluded from
 * the denominator. Zero available signals → null.
 */
export function computeCompositeScore(inputs: PulseInputs): number | null {
  const { stats, adrCount, patternCount } = inputs;

  let weightedSum = 0;
  let totalWeight = 0;

  // Gates signal — excluded when pass+fail === 0 (no gate data) or stats null
  if (stats !== null) {
    const { pass_count, fail_count } = stats.gate_pass_rate;
    const total = pass_count + fail_count;
    if (total > 0) {
      const gateScore = (pass_count / total) * 100;
      weightedSum += WEIGHT_GATES * gateScore;
      totalWeight += WEIGHT_GATES;
    }

    // Coverage signal
    const coverageScore = stats.artifact_coverage_pct;
    weightedSum += WEIGHT_COVERAGE * coverageScore;
    totalWeight += WEIGHT_COVERAGE;
  }

  // Decisions signal — always available from artifact list
  const decisionScore = Math.min(adrCount / 5, 1) * 100;
  weightedSum += WEIGHT_DECISIONS * decisionScore;
  totalWeight += WEIGHT_DECISIONS;

  // Graph signal — excluded when plan-gated (patternCount === null)
  if (patternCount !== null) {
    const graphScore = Math.min(patternCount / 10, 1) * 100;
    weightedSum += WEIGHT_GRAPH * graphScore;
    totalWeight += WEIGHT_GRAPH;
  }

  if (totalWeight === 0) return null;

  return weightedSum / totalWeight;
}

// ---------------------------------------------------------------------------
// extractAdrStatus
// ---------------------------------------------------------------------------

const STATUS_REGEX = /^status:\s*(accepted|draft|deprecated|superseded)\b/im;

/**
 * Extracts ADR status from content_summary via frontmatter regex (D2).
 * Falls back to "draft" when no match.
 */
export function extractAdrStatus(contentSummary: string | null): AdrStatus {
  if (!contentSummary) return "draft";
  const match = STATUS_REGEX.exec(contentSummary);
  if (!match) return "draft";
  return match[1].toLowerCase() as AdrStatus;
}

// ---------------------------------------------------------------------------
// extractSupersededBy
// ---------------------------------------------------------------------------

const SUPERSEDED_BY_REGEX = /^superseded[_\s]by:\s*(adr-\d+)/im;

/**
 * Extracts the superseded-by ADR reference from content_summary (D2).
 * Returns null when not found.
 */
export function extractSupersededBy(contentSummary: string | null): string | null {
  if (!contentSummary) return null;
  const match = SUPERSEDED_BY_REGEX.exec(contentSummary);
  return match ? match[1] : null;
}

// ---------------------------------------------------------------------------
// buildAttentionItems
// ---------------------------------------------------------------------------

/**
 * Generates up to 3 attention items from computed pulse state + pending
 * gate count. Warn items surface first; max 3 returned.
 */
export function buildAttentionItems(
  pulse: PulseData,
  pendingGateCount: number,
): AttentionItem[] {
  const candidates: AttentionItem[] = [];

  // Warn: pending gates waiting for approval
  if (pendingGateCount > 0) {
    candidates.push({
      key: "pendingGates",
      message: "projectPortal.governance.pulse.attention.pendingGates",
      severity: "warn",
    });
  }

  // Warn: overall score is poor
  if (pulse.score !== null && pulse.score < 50) {
    candidates.push({
      key: "lowScore",
      message: "projectPortal.governance.pulse.attention.lowScore",
      severity: "warn",
    });
  }

  // Info: no ADRs recorded yet
  if (pulse.adrCount === 0) {
    candidates.push({
      key: "noAdrs",
      message: "projectPortal.governance.pulse.attention.noAdrs",
      severity: "info",
    });
  }

  // Info: low coverage
  if (pulse.coveragePct !== null && pulse.coveragePct < 50) {
    candidates.push({
      key: "lowCoverage",
      message: "projectPortal.governance.pulse.attention.lowCoverage",
      severity: "info",
    });
  }

  // Sort: warn before info, then slice to max 3
  candidates.sort((a, b) => {
    if (a.severity === b.severity) return 0;
    return a.severity === "warn" ? -1 : 1;
  });

  return candidates.slice(0, 3);
}
